import {
  type AppRouteKey,
  useAccessControl,
  useAppNavigation,
  useCurrentUser,
  useUserAuth,
} from '@billx/api';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { logger } from '@/lib/logger';

import { useNavigateWithParams } from './useNavigateWithParams';

const getRouteKeyFromPath = (path: string): AppRouteKey | null => {
  if (path.startsWith('/login')) return 'login';
  if (path.startsWith('/dashboard')) return 'dashboard';
  if (path.startsWith('/staff')) return 'staff';
  if (path.startsWith('/user/select')) return 'select';
  if (path.startsWith('/user')) return 'user';
  if (path.startsWith('/invitations')) return 'invitations';
  if (path.startsWith('/onboarding')) return 'onboarding';
  return null;
};

const getPathFromRouteKey = (routeKey: AppRouteKey): string => {
  switch (routeKey) {
    case 'login':
      return '/login';
    case 'dashboard':
      return '/dashboard';
    case 'staff':
      return '/staff';
    case 'select':
      return '/user/select';
    case 'user':
      return '/user';
    case 'invitations':
      return '/invitations';
    case 'onboarding':
      return '/onboarding';
    default:
      return '/login';
  }
};

export const useAuthNavigation = () => {
  const location = useLocation();
  const navigate = useNavigateWithParams();

  const {
    isLoading: isNavLoading,
    canAccessRoute,
    getFallbackRoute,
  } = useAppNavigation();
  const { error: userError } = useCurrentUser();
  const { error: accessControlError } = useAccessControl();
  const { isLoggingOut } = useUserAuth();

  const isLoading = isNavLoading || isLoggingOut;
  const error = userError || accessControlError;

  useEffect(() => {
    if (isLoading) return;

    const currentPath = location.pathname;

    // For nested routes, check if the base path is accessible
    const pathSegments = currentPath.split('/').filter(Boolean);
    const basePath =
      pathSegments.length > 0 ? `/${pathSegments[0]}` : currentPath;

    const currentRouteKey = getRouteKeyFromPath(basePath);
    const canAccessCurrentPath = currentRouteKey
      ? canAccessRoute(currentRouteKey)
      : false;

    if (!canAccessCurrentPath) {
      const fallbackRouteKey = getFallbackRoute();
      const targetPath = getPathFromRouteKey(fallbackRouteKey);

      logger.debug('nav fallback', {
        from: currentPath,
        to: targetPath,
        fallbackRouteKey,
      });

      if (targetPath && targetPath !== currentPath && targetPath !== basePath) {
        navigate(targetPath);
      }
    }
  }, [
    isLoading,
    location.pathname,
    navigate,
    canAccessRoute,
    getFallbackRoute,
  ]);

  return {
    isLoading,
    error,
  };
};
